import { GetServerSideProps, GetStaticPropsContext } from 'next';
import dynamic from 'next/dynamic';
import { getCurrentLocale } from '../../utils/getCurrentLocale';
import {
  apolloClient,
  defaultQueryResult,
  getPageQuery,
} from '../../utils/apolloClient';
import { DISCOVERY_QUERY } from '../../graphql/queries';
import { RichText } from 'prismic-reactjs';
import React from 'react';
import { prepareSeoFromPageData } from '../../components/core/SEO/interface';
import { PageQuery } from '../../graphql/model/PageQuery';
import {
  DiscoveryQueryType,
  DiscoveryType,
} from '../../models/pageTypes/services/discovery';
import { Page } from '../../models/pageTypes/page';
import { Wrapper } from '../../styles/pages/styled/services/discovery.styles';
import { Parallax } from '../../components/sections/ProductDesign/Parallax/Parallax';
import { TitleAs } from '../../models/enums/titleAs';

const DynamicWhyYouNeedDiscovery = dynamic(
  () => import('../../components/sections/WhyYouNeedDiscovery')
);
const DynamicTestimonial = dynamic(
  () => import('../../components/sections/Testimonial')
);
const DynamicServiceTeam = dynamic(() =>
  import('../../components/sections/ServiceTeam/ServiceTeam').then(
    (mod) => mod.ServiceTeam
  )
);
const DynamicOurProcess = dynamic(() =>
  import('../../components/sections/OurProcess/OurProcess').then(
    (mod) => mod.OurProcess
  )
);

const Discovery = ({ data: { page } }: Page<DiscoveryType>) => {
  const section = page.sections.map((section) => ({
    title: RichText.asText(section.title),
    subtitle: RichText.asText(section.subtitle),
    description: RichText.asText(section.description),
    subDescription: RichText.asText(section.sub_description),
    subtitleInfo: RichText.asText(section.subtitle_info),
    image: section.image || {},
  }));

  return (
    <Wrapper className={'development'}>
      <Parallax
        name={'development'}
        page={page}
        sections={section}
        additionalClassName={'parallax-development'}
        backButtonColor={'white'}
      />
      <DynamicWhyYouNeedDiscovery
        title={RichText.asText(page.whyyouneeddiscovery_title)}
        subtitle={RichText.asText(page.whyyouneeddiscovery_title2)}
        description={RichText.asText(page.whyyouneeddiscovery_description)}
        steps={page.whyyouneeddiscovery_steps}
        titleAs={TitleAs.h3}
      />
      <DynamicOurProcess
        id={'discovery'}
        title={RichText.asText(page.discovery_process_title)}
        subtitle={page.discovery_process_subtitle}
        items={page.discovery_process_diagram}
        titleAs={TitleAs.h4}
      />
      <DynamicServiceTeam
        title={RichText.asText(page.discovery_team_title)}
        positions={page.team_positions}
        image={page.discovery_team_image}
        titleAs={TitleAs.h5}
      />
      <DynamicTestimonial
        additionalClassName={'product-design__testimonial'}
        title={RichText.asText(page.what_is_like_to_title)}
        quote={page.what_is_like_to_quote}
        caseName={RichText.asText(page.what_is_like_to_author)}
        casePosition={RichText.asText(page.what_is_like_to_person_position)}
        caseStudy
        titleAs={TitleAs.h6}
      />
    </Wrapper>
  );
};

export const getStaticProps: GetServerSideProps = async (
  context: GetStaticPropsContext
) => {
  const currentLanguage: string = getCurrentLocale(context);

  const {
    data: { allDiscoverys, ...defaultData },
  } = await apolloClient.query<PageQuery<DiscoveryQueryType>>(
    getPageQuery(
      {
        pageLang: currentLanguage,
      },
      'development_query',
      DISCOVERY_QUERY
    )
  );

  return {
    props: {
      data: {
        ...defaultQueryResult(defaultData),
        seo: prepareSeoFromPageData(allDiscoverys.edges[0].node),
        page: allDiscoverys.edges[0].node,
      },
    },
  };
};

export default Discovery;
